import * as React from 'react';
import { setHours } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { UTCDate } from '@date-fns/utc';
import type { ISODateRange, ISODateString } from 'shared/src/date-string-utils/types';
import {
  endOfYear,
  formatRange,
  startOfYear,
  toISODateString
} from 'shared/src/date-string-utils/date-utils';
import { Button } from '@/components/forms/ui/button';
import { Calendar } from '@/components/forms/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/forms/ui/popover';
import { cn } from '@/lib/utils';

type DateRangeFieldProps = {
  id?: string;
  label?: string;
  value: ISODateRange;
  onChange: (value: ISODateRange) => void;
  minDate: ISODateString;
  maxDate: ISODateString;
  description?: React.ReactNode;
  error?: string;
  warning?: string;
  disabled?: boolean;
};

export function DateRangeField({
  id,
  label,
  value,
  onChange,
  minDate,
  maxDate,
  description,
  error,
  warning,
  disabled = false
}: DateRangeFieldProps) {
  const minDateObj = setHours(new UTCDate(minDate), 12);
  const maxDateObj = setHours(new UTCDate(maxDate), 12);
  const selected = {
    from: setHours(new UTCDate(value.from), 12),
    to: setHours(new UTCDate(value.to), 12)
  };
  return (
    <div className="flex flex-col gap-1" id={id}>
      {label ? <label className="text-sm font-medium">{label}</label> : null}
      {description ? <span className="text-muted-foreground text-xs">{description}</span> : null}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn('flex pl-3 text-left font-normal', error && 'border border-red-500')}>
            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
            <span className="truncate text-blue-600">{formatRange(value)}</span>
            <div className="flex-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            defaultMonth={selected.from}
            mode="range"
            numberOfMonths={2}
            timeZone="UTC"
            disabled={{ before: minDateObj, after: maxDateObj }}
            startMonth={new UTCDate(startOfYear(minDate))}
            endMonth={new UTCDate(endOfYear(maxDate))}
            showOutsideDays={false}
            captionLayout="dropdown"
            selected={selected}
            onSelect={range => {
              if (range && range.from && range.to) {
                onChange({
                  from: toISODateString(range.from),
                  to: toISODateString(range.to)
                });
              }
            }}
          />
        </PopoverContent>
      </Popover>
      {error ? <span className="text-destructive text-sm">{error}</span> : null}
      {warning ? <span className="text-sm text-amber-700">{warning}</span> : null}
    </div>
  );
}
